import { getEnv } from "@istari-frontend/shared";

import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";

/**
 * Returns whether infosec is actually active on this deployment.
 *
 * `VITE_INFOSEC_ENABLED` only gates the FE feature shell; the registry's
 * `platform_infosec_settings.enabled` is the source of truth for whether the
 * API will accept `infosec_level_id` on writes. Submitting infosec fields when
 * the registry has infosec off is rejected with `PlatformInfosecLevelsNotEnabledError`,
 * which currently surfaces as an HTTP 404 (the error inherits from `NotFoundError`;
 * the backend may later reclassify it to 400/409) — so callers should hide /
 * suppress infosec inputs unless both gates agree.
 *
 * Surfaces that exist *to flip the registry switch on* (e.g. the admin infosec
 * settings page and its nav entry) should gate on `useInfosecFeatureEnabled`
 * instead — otherwise the page hides itself before an admin can enable it.
 */
export function usePlatformInfosecActive(): boolean {
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  // A failed fetch is treated as "infosec off" (fields hide); surface it so a
  // transient registry outage masquerading as disabled is at least observable.
  if (platformSettingsQuery.isError) {
    console.error("Failed to load platform infosec settings", platformSettingsQuery.error);
  }
  return useInfosecFeatureEnabled() && (platformSettingsQuery.data?.enabled ?? false);
}

/**
 * Returns whether the infosec feature shell is enabled on the FE.
 *
 * Use for surfaces that must remain reachable before the registry switch is
 * flipped — most importantly the admin infosec configuration page itself. For
 * everything else (showing infosec on a resource, submitting `infosec_level_id`
 * on writes), use `usePlatformInfosecActive`.
 */
export function useInfosecFeatureEnabled(): boolean {
  return getEnv("VITE_INFOSEC_ENABLED") === "true";
}
